import type { TourStep } from '@/components/ui/Tour';
import {
  CLUB_PAGE_TOUR_STEP_IDS,
  COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS,
  TOUR_CENTERED_STEP_SELECTOR,
  TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
} from '../tour-constants';
import { TourStepContent } from './StepContent';

export interface ClubCourtHireTourOptions {
  onEnterBookings?: () => void;
  onEnterRates?: () => void;
  onEnterSettings?: () => void;
  onOpenCreateRate?: () => void;
  onCloseCreateRate?: () => void;
  onFinish?: () => void;
}

const openCreateRateDrawer = (options: ClubCourtHireTourOptions) => () => {
  options.onEnterRates?.();
  options.onOpenCreateRate?.();
};

export function getClubCourtHireTourSteps(options: ClubCourtHireTourOptions = {}): TourStep[] {
  return [
    {
      content: (
        <TourStepContent
          title="Bookings tab"
          description="Review guest and member court rentals. Search by hirer and filter by status to reconcile the day."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_BOOKINGS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterBookings?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Rates tab"
          description="Rates define session length and price for public court hire. Create different rates per court or apply club-wide."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_RATES,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterRates?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Hire rates"
          description="Active rates appear on your public court hire page. Deactivate rates you no longer sell without deleting history."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES,
      position: 'top',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterRates?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Create a rate"
          description="Add a new hire rate — we'll walk through the form next."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES_NEW,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterRates?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Create a hire rate"
          description="Name the rate, set duration and price, and choose whether it applies to all courts or one court."
        />
      ),
      selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DRAWER,
      position: 'left',
      onStepEnter: openCreateRateDrawer(options),
      targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
      waitForPanelReady: true,
    },
    {
      content: (
        <TourStepContent
          title="Rate details"
          description="Duration must be at least 15 minutes. Toggle members-only when a rate should not appear to guests."
        />
      ),
      selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DETAILS,
      position: 'bottom',
      onStepEnter: openCreateRateDrawer(options),
      targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
      waitForPanelReady: true,
    },
    {
      content: (
        <TourStepContent
          title="Save the rate"
          description="Create the rate to make it available for court hire checkout. You can close without saving to continue the tour."
        />
      ),
      selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.SUBMIT,
      position: 'top',
      onStepEnter: openCreateRateDrawer(options),
      targetPollTimeoutMs: TOUR_DRAWER_TARGET_POLL_TIMEOUT_MS,
      waitForPanelReady: true,
    },
    {
      content: (
        <TourStepContent
          title="Settings tab"
          description="Enable guest checkout, set booking rules, and control how visitors book courts on your public site."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_TAB_SETTINGS,
      position: 'bottom',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterSettings?.();
      },
    },
    {
      content: (
        <TourStepContent
          title="Public booking options"
          description="Turn guest checkout on when you're ready to accept online court hire payments via Stripe Connect."
        />
      ),
      selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_SETTINGS,
      position: 'top',
      onStepEnter: () => {
        options.onCloseCreateRate?.();
        options.onEnterSettings?.();
      },
    },
  ];
}

/** @deprecated Use getClubCourtHireTourSteps for the unified page tour. */
export const clubCourtHireBookingsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Court hire bookings"
        description="Review guest and member court rentals. Search by hirer and filter by status to reconcile the day."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Bookings list"
        description="Each row shows when, which court, and who booked. Open a member's profile from their booking when needed."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_BOOKINGS,
    position: 'top',
  },
];

/** @deprecated Use getClubCourtHireTourSteps for the unified page tour. */
export const clubCourtHireRatesTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Hire rates"
        description="Rates define session length and price for public court hire. Create different rates per court or apply club-wide."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Rate list"
        description="Active rates appear on your public court hire page. Deactivate rates you no longer sell without deleting history."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES,
    position: 'top',
  },
  {
    content: (
      <TourStepContent
        title="Create a rate"
        description="Add a new hire rate when you're ready — we'll walk through the form on your first create."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_RATES_NEW,
    position: 'bottom',
  },
];

/** @deprecated Use getClubCourtHireTourSteps for the unified page tour. */
export const clubCourtHireSettingsTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Court hire settings"
        description="Enable guest checkout, set booking rules, and control how visitors book courts on your public site."
      />
    ),
    selectorId: TOUR_CENTERED_STEP_SELECTOR,
  },
  {
    content: (
      <TourStepContent
        title="Public booking options"
        description="Turn guest checkout on when you're ready to accept online court hire payments via Stripe Connect."
      />
    ),
    selectorId: CLUB_PAGE_TOUR_STEP_IDS.COURT_HIRE_SETTINGS,
    position: 'top',
  },
];

export const courtHireRateCreateTourSteps: TourStep[] = [
  {
    content: (
      <TourStepContent
        title="Create a hire rate"
        description="Name the rate, set duration and price, and choose whether it applies to all courts or one court."
      />
    ),
    selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DRAWER,
    position: 'left',
  },
  {
    content: (
      <TourStepContent
        title="Rate details"
        description="Duration must be at least 15 minutes. Toggle members-only when a rate should not appear to guests."
      />
    ),
    selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.DETAILS,
    position: 'bottom',
  },
  {
    content: (
      <TourStepContent
        title="Save the rate"
        description="Create the rate to make it available for court hire checkout."
      />
    ),
    selectorId: COURT_HIRE_RATE_CREATE_TOUR_STEP_IDS.SUBMIT,
    position: 'top',
  },
];
